<template>
  <div v-if="loading">
    <AwIcon name="i-ph-spinner-gap-bold" color="var(--gray-3)" class="animate-spin size-4" />
  </div>
  <div v-else>
    <div v-if="isError || (display === 'all' && extensionsCount === 0 && modificationsCount === 0)">-</div>
    <div v-else-if="display === 'extensions'">{{ extensionsCount }}/{{ EXTENSION_AMENDMENT_RULES.MAX_ALLOWED }}</div>
    <div v-else class="flex items-center flex-row gap-1">
      <div v-if="modificationsCount > 0" class="flex items-center flex-row">
        <AwIcon name="i-ph-files-duotone" class="color-secondary-800" />
        {{ modificationsCount }}
      </div>
      <div v-if="extensionsCount > 0" class="flex items-center flex-row gap-0.5">
        <AwIcon name="i-ph-file-plus" class="color-secondary-800" />
        {{ extensionsCount }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "~/composables/useApi";
import type { AmendmentType, ContractStatus } from "@asap/shared/utils";
import type { ContractTableRow } from "@asap/shared/types/contract";
import { EXTENSION_AMENDMENT_RULES } from "~/utils/contract";

type Props = {
  initialAmendments?: ContractTableRow[];
  contractKey: number;
  isLoading?: boolean;
  display?: "extensions" | "all";
};

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  initialAmendments: undefined,
  display: "all",
});

const { invoke } = useApi();
const amendments = ref<ContractTableRow[]>([]);

const loading = ref(props.isLoading);
const isError = ref(false);

watch(
  () => props.isLoading,
  (val) => {
    loading.value = val;
  }
);

const extensionsCount = ref(0);
const modificationsCount = ref(0);

const countAmendments = (amendments: ContractTableRow[], type: AmendmentType, excludedStatus?: ContractStatus[]) => {
  return amendments.filter(
    (amendment) =>
      amendment.amendment_type === type && (excludedStatus ? !excludedStatus.includes(amendment.status) : true)
  ).length;
};

const fetchAmendments = async () => {
  loading.value = true;
  const response = await invoke({
    path: "contracts/table",
    method: "GET",
    query: {
      contract_key: props.contractKey,
      is_amendment: true,
    },
  });
  if (!response.success) {
    isError.value = true;
    throw new Error(response.error.message);
  }
  if (response.data) amendments.value = response.data;
  extensionsCount.value = countAmendments(response.data, "extension", ["draft", "refused", "cancelled"]);
  modificationsCount.value = countAmendments(response.data, "modification");
  loading.value = false;
};

onMounted(async () => {
  if (!props.contractKey) {
    return;
  }

  if (props.initialAmendments === undefined) {
    await fetchAmendments();
  } else {
    amendments.value = props.initialAmendments;
    extensionsCount.value = countAmendments(props.initialAmendments, "extension", ["draft", "refused", "cancelled"]);
    modificationsCount.value = countAmendments(props.initialAmendments, "modification");
  }
});

watch(
  () => props.initialAmendments,
  () => {
    if (props.initialAmendments !== undefined) {
      amendments.value = props.initialAmendments;
      extensionsCount.value = countAmendments(props.initialAmendments, "extension", ["draft", "refused", "cancelled"]);
      modificationsCount.value = countAmendments(props.initialAmendments, "modification");
    }
  },
  { deep: true }
);
</script>
